import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { adminApi } from "../services/api";
import { useI18n, useMoneyFormatter } from "@expertloop/i18n/react";

interface OrderStats {
	_id: string;
	count: number;
	totalAmount: number;
}

interface GuestDetails {
	id: string;
	email: string;
	displayName: string;
	role: "guest";
	isGuest: true;
	firstSeen: string;
	lastSeen: string;
	stats: {
		totalChats: number;
		chatsByStatus: Record<string, number>;
		orders: OrderStats[];
	};
}

interface GuestChat {
	id: string;
	expertId: string;
	clientEmail: string;
	clientName?: string;
	status: string;
	createdAt: string;
	expert: {
		name: string;
		email: string;
	};
	product: {
		name: string;
		price: number;
	};
	order?: {
		amount: number;
		status: string;
		mediationRequestedAt?: string;
		/** Date de la décision du support : la médiation est alors close. */
		supportDecisionAt?: string;
	};
}

export function GuestDetailPage() {
	const { t } = useI18n();
	const { formatCents, formatUnits } = useMoneyFormatter();
	const { email } = useParams<{ email: string }>();
	const [guest, setGuest] = useState<GuestDetails | null>(null);
	const [chats, setChats] = useState<GuestChat[]>([]);
	const [loading, setLoading] = useState(true);
	const [chatsLoading, setChatsLoading] = useState(false);
	const [error, setError] = useState("");

	const decodedEmail = email ? decodeURIComponent(email) : "";

	useEffect(() => {
		const fetchGuest = async () => {
			if (!email) return;
			setLoading(true);
			try {
				const result = await adminApi.get<GuestDetails>(`/admin/users/guest/${email}`);
				setGuest(result);
			} catch (err) {
				setError(err instanceof Error ? err.message : t("adminGuestDetail.loadFailed"));
			} finally {
				setLoading(false);
			}
		};

		fetchGuest();
	}, [email]);

	useEffect(() => {
		const fetchChats = async () => {
			if (!email) return;
			setChatsLoading(true);
			try {
				const result = await adminApi.get<{ chats: GuestChat[] }>(
					`/admin/users/guest/${email}/chats`,
				);
				setChats(result.chats);
			} catch {
				// Ignore chat loading errors
			} finally {
				setChatsLoading(false);
			}
		};

		fetchChats();
	}, [email]);

	const formatDate = (date?: string) => {
		if (!date) return t('adminUsersPage.notAvailable');
		return new Date(date).toLocaleString();
	};

	const getOrderStatsTotal = (stats: OrderStats[]): { count: number; amount: number } => {
		return stats.reduce(
			(acc, stat) => ({
				count: acc.count + stat.count,
				amount: acc.amount + stat.totalAmount,
			}),
			{ count: 0, amount: 0 },
		);
	};

	if (loading) {
		return <div className="loading">{t('adminGuestDetail.loading')}</div>;
	}

	if (error || !guest) {
		return (
			<div>
				<div className="detail-header">
					<Link to="/users" className="back-link">
						{t('adminGuestDetail.backToUsers')}
					</Link>
				</div>
				<div className="error-message">{error || t('adminGuestDetail.notFound')}</div>
			</div>
		);
	}

	const orderStats = getOrderStatsTotal(guest.stats.orders);

	return (
		<div>
			<div className="detail-header">
				<Link to="/users" className="back-link">
					{t('adminGuestDetail.backToUsers')}
				</Link>
				<h1>{guest.displayName || decodedEmail}</h1>
				<div style={{ display: "flex", gap: "0.25rem", marginLeft: "1rem" }}>
					<span className="status-badge" style={{ background: "#f3e8ff", color: "#7c3aed" }}>
						{t('adminUsersPage.guest')}
					</span>
				</div>
			</div>

			{/* Guest Info Cards */}
			<div className="detail-grid">
				{/* Basic Info */}
				<div className="info-card">
					<h3>{t('adminGuestDetail.basicInfo')}</h3>
					<div className="info-row">
						<span className="info-label">{t('auth.login.email')}</span>
						<span className="info-value">{guest.email}</span>
					</div>
					{guest.displayName && (
						<div className="info-row">
							<span className="info-label">{t('profile.displayName')}</span>
							<span className="info-value">{guest.displayName}</span>
						</div>
					)}
					<div className="info-row">
						<span className="info-label">{t('adminGuestDetail.authProvider')}</span>
						<span className="info-value" style={{ color: "var(--color-gray-400)" }}>
							{t('adminUsersPage.notAvailable')}
						</span>
					</div>
					<div className="info-row">
						<span className="info-label">{t('adminGuestDetail.emailVerified')}</span>
						<span className="info-value" style={{ color: "var(--color-gray-400)" }}>
							{t('adminUsersPage.notAvailable')}
						</span>
					</div>
					<div className="info-row">
						<span className="info-label">{t('adminGuestDetail.memberSince')}</span>
						<span className="info-value" style={{ color: "var(--color-gray-400)" }}>
							{t('adminUsersPage.notAvailable')}
						</span>
					</div>
				</div>

				{/* Activity Stats */}
				<div className="info-card">
					<h3>{t('adminGuestDetail.activity')}</h3>
					<div className="info-row">
						<span className="info-label">{t('adminGuestDetail.firstSeen')}</span>
						<span className="info-value">{formatDate(guest.firstSeen)}</span>
					</div>
					<div className="info-row">
						<span className="info-label">{t('adminGuestDetail.lastSeen')}</span>
						<span className="info-value">{formatDate(guest.lastSeen)}</span>
					</div>
					<div className="info-row">
						<span className="info-label">{t('adminGuestDetail.totalChats')}</span>
						<span className="info-value">{guest.stats.totalChats}</span>
					</div>
					<div className="info-row">
						<span className="info-label">{t('adminGuestDetail.orders')}</span>
						<span className="info-value">
							{orderStats.count > 0
								? `${orderStats.count} (${formatCents(orderStats.amount, "eur")})`
								: t('adminGuestDetail.none')}
						</span>
					</div>
				</div>

				{/* Chat Status Breakdown */}
				<div className="info-card">
					<h3>{t('adminGuestDetail.chatStatusBreakdown')}</h3>
					{Object.entries(guest.stats.chatsByStatus).map(([status, count]) => (
						<div className="info-row" key={status}>
							<span className="info-label" style={{ textTransform: "capitalize" }}>
								{status.replace("_", " ")}
							</span>
							<span className="info-value">{count}</span>
						</div>
					))}
				</div>
			</div>

			{/* Guest's Chats */}
			<div className="card">
				<div className="card-header">
					<h2>{t('adminGuestDetail.chatsCount', { count: chats.length })}</h2>
				</div>
				{chatsLoading ? (
					<div className="loading">{t('chatsPage.loading')}</div>
				) : chats.length === 0 ? (
					<div className="empty-state">
						<h3>{t('adminGuestDetail.noChats')}</h3>
						<p>{t('adminGuestDetail.noChatsBody')}</p>
					</div>
				) : (
					<div className="table-container">
						<table>
							<thead>
								<tr>
									<th>{t('adminMediations.expert')}</th>
									<th>{t('adminChatsPage.product')}</th>
									<th>{t('adminChatsPage.status')}</th>
									<th>{t('adminChatsPage.order')}</th>
									<th>{t('adminChatsPage.created')}</th>
									<th>{t('adminChatsPage.actions')}</th>
								</tr>
							</thead>
							<tbody>
								{chats.map((chat) => (
									<tr key={chat.id}>
										<td>
											<div>{chat.expert.name}</div>
											<div style={{ fontSize: "0.75rem", color: "var(--color-gray-500)" }}>
												{chat.expert.email}
											</div>
										</td>
										<td>
											<div>{chat.product.name}</div>
											<div style={{ fontSize: "0.75rem", color: "var(--color-gray-500)" }}>
												{formatUnits(chat.product.price, "eur")}
											</div>
										</td>
										<td>
											<span className={`status-badge ${chat.status}`}>
												{chat.status}
											</span>
										</td>
										<td>
											{chat.order ? (
												<div>
													<span className={`status-badge ${chat.order.status}`}>
														{chat.order.status}
													</span>
													{chat.order.mediationRequestedAt && (
														<span
															className={`status-badge ${chat.order.supportDecisionAt ? "completed" : "mediation"}`}
															style={{ marginLeft: "0.25rem" }}
														>
															{chat.order.supportDecisionAt
																? t('adminChatsPage.mediationResolved')
																: t('adminChatsPage.mediation')}
														</span>
													)}
												</div>
											) : (
												<span style={{ color: "var(--color-gray-400)" }}>{t('adminChatsPage.noOrder')}</span>
											)}
										</td>
										<td>{formatDate(chat.createdAt)}</td>
										<td>
											<Link to={`/chats/${chat.id}`} className="table-link">
												{t('chatsPage.view')}
											</Link>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>
		</div>
	);
}
